import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Divider
} from "@mui/material";
import { alpha, useTheme } from '@mui/material/styles';
import logo from '../assets/nttlogoblanco1.png';
import awsLogo from '../assets/aws.png';
import azureLogo from '../assets/Azure.png';
import { useToast } from "../components/ToastContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../services/authContext";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { api } from "../services/api";
import gainwelllogo from '../assets/Gainwell_Logo_white_RGB.png';
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import PropTypes from "prop-types";
import { Dialog, DialogContent } from "@mui/material";
import AccountSelector from "../pages/AccountSelector";
import { useSelectedProvider } from "../services/selectedProiderContext";
import { useConfig } from "../services/ConfigContext";
import ThemeModeToggle from "./ThemeModeToggle";

const dotBlink = {
  "@keyframes dotBlink": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.2 },
    "100%": { opacity: 1 },
  },
};

// Converted static colors into responsive semantic theme tokens
const getIntegrationStyles = (isHealthy) => {
  if (isHealthy === true) {
    return {
      bgcolor: "success.main",
      text: "Active",
      blink: true,
    };
  }
  if (isHealthy === false) {
    return {
      bgcolor: "error.main",
      text: "Down",
      blink: false,
    };
  }
  return {
    bgcolor: "text.disabled",
    text: "Unknown",
    blink: false,
  };
};

const getIntegration = (data, name) =>
  data?.integrations?.find((i) => i.name === name);

/* ----------------------------------------------------
   Integration Badge (Parameterized & Theme-Aware)
---------------------------------------------------- */

const IntegrationStatusBadge = ({ label, isHealthy }) => {
  const styles = getIntegrationStyles(isHealthy);

  return (
    <Box
      sx={{
        ...(styles.blink ? dotBlink : {}),
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          px: 1.2,
          py: 0.3,
          borderRadius: "0 0 20px 20px",
          bgcolor: styles.bgcolor, // Semantic background assignment
          color: "common.white",   // White text rule on brand surfaces
          fontSize: "11px",
          fontWeight: 600,
          letterSpacing: "0.4px",
          boxShadow: (theme) => theme.shadows[2],
          whiteSpace: "nowrap",
          marginBottom: '2px',
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "common.white",
            animation: styles.blink ? "dotBlink 1.2s infinite" : "none",
          }}
        />
        {label} {styles.text}
      </Box>
    </Box>
  );
};

IntegrationStatusBadge.propTypes = {
  label: PropTypes.string.isRequired,
  isHealthy: PropTypes.bool,
};

export default function Header({ toggleTheme, pendingCount }) {
  const theme = useTheme();
  const toast = useToast();
  const {
    selectedAccount,
  } = useSelectedProvider();
  const navigate = useNavigate();
  const { isAuthenticated, setIsAuthenticated, user } = useAuth();

  const { instance, inProgress } = useMsal();
  const { config } = useConfig();
  const isGainwellLogo = config?.isGainwellLogo;

  const [integrationHealth, setIntegrationHealth] = useState(null);
  const [openProviderDialog, setOpenProviderDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openUserMenu = Boolean(anchorEl);

  const activeAccountId = selectedAccount.accountId;
  const selectedProvider = selectedAccount.provider;
  const username = sessionStorage.getItem("username");
  const email = sessionStorage.getItem("emailId");
  const [providerDialogMode, setProviderDialogMode] = useState("forced");
  const Accountname = selectedAccount.accountName;
  /* ---------- Fetch Integration Health ---------- */
  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const res = await api.get("/api/Resources/IntegrationStatus");
        setIntegrationHealth(res.data);
      } catch (err) {
        console.error("Integration health fetch failed", err);
      }
    };

    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const github = getIntegration(integrationHealth, "github");
  const serviceNow = getIntegration(integrationHealth, "servicenow");

  const roleName =
    sessionStorage.getItem("rolename") ||
    sessionStorage.getItem("role") ||
    "";

  const isValidAccountId = (id) =>
    Boolean(id) && id !== "null" && id !== "undefined";

  useEffect(() => {
    if (isAuthenticated && (!isValidAccountId(activeAccountId) || !selectedProvider)) {
      setProviderDialogMode("forced");
      setOpenProviderDialog(true);
    }
  }, [isAuthenticated, activeAccountId, selectedProvider]);

  const handleLogout = async () => {
    try {
      const userId = sessionStorage.getItem('userId');
      const isSSOLogin = localStorage.getItem('ssoLogin') === 'true';
      const correlationId = crypto.randomUUID();

      if (inProgress !== InteractionStatus.None) {
        console.warn("MSAL interaction in progress. Logout skipped.");
        return;
      }

      if (isSSOLogin) {
        if (userId) {
          await api.post(`/Account/logout/${userId}`, {}, {
            headers: { 'X-CORRELATION-ID': correlationId },
          });
        }
      } else {
        await api.post(`/Account/Logout`, {}, {
          headers: { 'X-CORRELATION-ID': correlationId },
        });
      }

      localStorage.clear();
      sessionStorage.clear();
      setIsAuthenticated(false);

      if (isSSOLogin) {
        await instance.logoutRedirect({ postLogoutRedirectUri: '/login' });
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error(" Logout failed:", error);
      toast.error("Logout failed. Please try again.");
      localStorage.clear();
      sessionStorage.clear();
      setIsAuthenticated(false);
      navigate('/login');
    }
  };

  const renderLogo = () => {
    if (selectedProvider) {
      const lowerCaseProvider = selectedProvider.toLowerCase();
      if (lowerCaseProvider === 'aws') {
        return <img src={awsLogo} alt="AWS Logo" style={{ width: 28, marginRight: theme.spacing(1), marginTop: theme.spacing(0.5) }} />;
      } else if (lowerCaseProvider === 'azure') {
        return <img src={azureLogo} alt="Azure Logo" style={{ width: 25, height: 25, marginRight: theme.spacing(1), marginTop: theme.spacing(0.5) }} />;
      }
    }
    return null;
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        position: "relative",
        bgcolor: theme.palette.background.sideBg,
        color: theme.palette.text.primary,
        boxShadow: "none",
        backgroundImage: 'none'
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", px: theme.spacing(2) }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(0.5) }}>
          <img src={logo} alt="Logo" style={{ height: 30 }} />
          <Typography variant="body2" sx={{ mx: theme.spacing(1), color: theme.palette.text.header }}>|</Typography>
          {isGainwellLogo && (
            <>
              <img src={gainwelllogo} alt="Gainwell" style={{ height: 30 }} />
              <Typography variant="body2" sx={{ mx: theme.spacing(1), color: theme.palette.text.header }}>|</Typography>
            </>
          )}
          <Typography variant="h5" sx={{ color: theme.palette.text.header }}>
            Cloud Management Platform
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', height: '100%' }}>
          {selectedProvider && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                px: 3,
                py: 0.5,
                // Changed raw overlay styles to be dynamically theme-driven
                backgroundColor: alpha(theme.palette.text.primary, 0.2),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.text.primary, 0.3),
                },
              }}
              onClick={() => {
                setOpenProviderDialog(true);
                setProviderDialogMode("switch");
              }}
            >
              {renderLogo()}
              {/* <Typography
                variant="subtitle1"
                sx={{ color: theme.palette.text.header, fontSize: '14px' }}
              >
                {selectedProvider?.toLowerCase() === 'azure' ? subscriptionId : accountId}
              </Typography> */}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  lineHeight: 1.2,
                }}
              >

                <Tooltip
                  title={
                    <Box>
                      <Typography fontSize="12px">
                        {/* <strong>Account:</strong> */}
                        {Accountname}
                      </Typography>
                      <Typography fontSize="12px">
                        {/* <strong>ID:</strong> */}
                        {" "}
                        {activeAccountId}
                      </Typography>
                    </Box>
                  }
                  arrow>
                  <Typography
                    sx={{
                      color: theme.palette.text.header,
                      fontSize: "14px",
                      fontWeight: 600,
                      maxWidth: "226px", // adjust width as needed
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                  >
                    {Accountname}
                  </Typography>


                  {/* <Typography
    sx={{
      color: theme.palette.text.header,
      fontSize: "14px",
      fontWeight: 600,
    }}
  >
    {Accountname}
  </Typography> */}

                  <Typography
                    sx={{
                      color: theme.palette.text.header,
                      fontSize: "12px",
                    }}
                  >
                    {activeAccountId}
                  </Typography>
                </Tooltip>



              </Box>

            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(2) }}>
            <IconButton onClick={handleAvatarClick}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "transparent",
                  boxShadow: `0 0 0 1px ${theme.palette.divider}`,
                  transition: "box-shadow 0.2s ease",
                  "&:hover": {
                    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
                  },
                }}
              >
                <AccountCircleIcon
                  sx={{
                    width: 25,
                    height: 25,
                    color: theme.palette.text.header // Anchored safely to header text color
                  }}
                />
              </Avatar>
            </IconButton>
          </Box>
        </Box>
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={openUserMenu}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 1.5,
              minWidth: 280,
              borderRadius: 3,
              boxShadow: (theme) => theme.shadows[8],
              backgroundColor: "background.paper",
              border: (theme) => `1px solid ${theme.palette.divider}`,
              overflow: "visible",
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 18,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              },
            },
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mb: 1.5,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 40 }} />
          </Avatar>

          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {username}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {email}
          </Typography>

          {user?.preferred_username && (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
              {user.preferred_username}
            </Typography>
          )}

          <Box
            sx={{
              mt: 2,
              px: 2,
              py: 0.5,
              borderRadius: 999,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "grey.100",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Role: {roleName}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 0 }} />

        {/* Logout Button */}
        <Box sx={{ p: 1 }}>
          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              handleLogout();
            }}
            sx={{
              borderRadius: 2,
              gap: 1.5,
              py: 1.2,
              justifyContent: "center",
              color: "text.primary",
              "&:hover": {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <LogoutIcon fontSize="small" />
            <Typography fontWeight={600} fontSize={14}>
              Logout
            </Typography>
          </MenuItem>
        </Box>
      </Menu>
      <Dialog
        open={openProviderDialog}
        onClose={(event, reason) => {
          if (providerDialogMode === "forced") return;
          setOpenProviderDialog(false);
        }}
        disableEscapeKeyDown={providerDialogMode === "forced"}
        fullScreen
        slotProps={{
          paper: {
            sx: { m: 5, height: '80vh', overflow: "hidden", borderRadius: 1, bgcolor: 'background.paper' }
          },
          backdrop: {
            sx: { backgroundColor: alpha('#000000', 0.7) }
          }
        }}
      >
        <DialogContent sx={{ p: 0, overflow: "hidden", borderRadius: 4 }}>
          <AccountSelector
            mode="switch"
            onSuccess={(provider, accountId) => {
              setOpenProviderDialog(false);
              if (provider && accountId) {
                navigate('/Resources', { state: { provider, accountId } });
              }
            }}
            autoNavigate={false}
          />
        </DialogContent>
      </Dialog>

      {/* --------- INTEGRATION STATUS (YOUR ORIGINAL POSITION) --------- */}
      <Box
        sx={{
          position: "absolute",
          bottom: -24,
          right: 20,
          gap: 1.5,
          display: "flex",
          zIndex: 10,
          width: "100%",
          justifyContent: "flex-end",
          minHeight: "24px"
        }}
      >
        {github && (
          <IntegrationStatusBadge
            label="GitHub"
            isHealthy={github.isHealthy}
          />
        )}

        {serviceNow && (
          <IntegrationStatusBadge
            label="ServiceNow"
            isHealthy={serviceNow.isHealthy}
          />
        )}
      </Box>
    </AppBar>
  );
}
Header.propTypes = {
  toggleTheme: PropTypes.func.isRequired,
  pendingCount: PropTypes.number,
};
